/** Sign-in: enter your personal code once per device (it works on any number of devices). */
import { CODE_LENGTH, formatCode, normalizeCode, type SignInResponse } from '../../shared/api.ts';
import { apiJson, describeError } from '../app/api.ts';
import { setSession } from '../app/state.ts';
import { adoptDevice } from '../app/sync.ts';
import { requestPersistence } from '../store/idb.ts';
import { h } from './dom.ts';

function deviceName(): string {
  const ua = navigator.userAgent;
  const standalone = matchMedia('(display-mode: standalone)').matches ? ' (Home Screen)' : '';
  if (/iPad|Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'iPad' + standalone;
  if (/iPhone/.test(ua)) return 'iPhone' + standalone;
  if (/Macintosh/.test(ua)) return 'Mac';
  return 'Device' + standalone;
}

export function renderSignIn(root: HTMLElement, onDone: () => void): () => void {
  const input = h('input', { class: 'input code', type: 'text', inputmode: 'text', autocomplete: 'off', autocapitalize: 'characters', autocorrect: 'off', spellcheck: false, placeholder: 'XXXX-XXXX-XXXX', 'aria-label': 'Sign-in code', enterkeyhint: 'go', maxlength: 14 });
  const error = h('div', { class: 'error', role: 'alert' });
  const submit = h('button', { class: 'btn primary large block', type: 'submit' }, 'Sign in');
  const HELP = 'Enter your personal sign-in code. It works on every device, and this device stays signed in.';
  const status = h('p', { class: 'help' }, HELP);
  const prefill = new URLSearchParams(location.search).get('code') ?? (location.hash.startsWith('#code=') ? location.hash.slice(6) : '');
  if (prefill) input.value = formatCode(normalizeCode(prefill).slice(0, CODE_LENGTH));

  input.addEventListener('input', () => {
    const norm = normalizeCode(input.value).slice(0, CODE_LENGTH);
    input.value = formatCode(norm);
    error.textContent = '';
  });
  const form = h(
    'form',
    {
      class: 'card-panel',
      onSubmit: async (e: Event) => {
        e.preventDefault();
        const code = normalizeCode(input.value);
        if (code.length !== CODE_LENGTH) {
          error.textContent = `Codes are ${CODE_LENGTH} characters.`;
          return;
        }
        submit.disabled = true;
        status.textContent = 'Checking…';
        try {
          const res = await apiJson<SignInResponse>('/api/auth/signin', { method: 'POST', body: JSON.stringify({ code, device: deviceName() }) }, { auth: false });
          await adoptDevice(res.user.id);
          await setSession({ token: res.token, user: res.user, code });
          void requestPersistence();
          onDone();
        } catch (err) {
          error.textContent = describeError(err);
          status.textContent = HELP;
          submit.disabled = false;
        }
      },
    },
    h('div', { class: 'brand' }, h('img', { class: 'mark', src: '/icons/icon-192.png', alt: '', width: 68, height: 68 }), h('h1', null, 'MapMark'), h('div', { class: 'tag' }, 'Plan markup for special inspectors')),
    h('div', { class: 'field' }, h('label', { for: 'code' }, 'Sign-in code'), input),
    error,
    submit,
    status,
  );
  input.id = 'code';
  const screen = h('div', { class: 'screen' }, h('div', { class: 'signin' }, form));
  root.append(screen);
  requestAnimationFrame(() => input.focus());
  return () => screen.remove();
}
